// Three quick-start shapes for a new show. Prefills drafts only.
//
// FortNine confirmation mail is still pinned by activation id at request-code
// time. A saved otp_email_template column is a later pass. Not picking a
// template is the blank start — there is no fourth type.

import type { CaptureOffer } from "./captureOffer.js";
import { applyPreset, type LegalSetup } from "./legalSetup.js";
import { withRideLegalDocuments } from "./rideDocuments.js";

export const SHOW_TEMPLATES = ["promo_game", "lead_capture", "onsite_hall"] as const;
export type ShowTemplateId = (typeof SHOW_TEMPLATES)[number];

export const SHOW_TEMPLATE_COPY: Readonly<
  Record<ShowTemplateId, { readonly title: string; readonly body: string }>
> = {
  promo_game: {
    title: "Promo game",
    body: "Public and onsite. First name, last name, email and a leaderboard name. Game legal pack.",
  },
  lead_capture: {
    title: "Lead capture",
    body: "Public only. First name, last name and email. Standard legal pack. Privacy as a notice.",
  },
  onsite_hall: {
    title: "Onsite hall",
    body: "Onsite only. First name, last name and email. Same legal pack as lead capture. No mailbox.",
  },
};

export function isShowTemplateId(value: string): value is ShowTemplateId {
  return value === "promo_game" || value === "lead_capture" || value === "onsite_hall";
}

/**
 * Remembered in this module for the walk from Add activation to
 * Registration in the same tab. A template id is not a credential. A reload
 * falls back to the picker on a show that still has no instruction.
 */
const pending = new Map<string, ShowTemplateId>();

export function storeShowTemplate(activationId: string, template: ShowTemplateId): void {
  pending.set(activationId, template);
}

/** One-shot. Reading clears it so a later visit is not silently reshaped. */
export function takeShowTemplate(activationId: string): ShowTemplateId | null {
  const value = pending.get(activationId);
  if (value === undefined) return null;
  pending.delete(activationId);
  return value;
}

export function fieldsForTemplate(template: ShowTemplateId): readonly string[] {
  if (template === "promo_game") return ["first_name", "last_name", "contact_email", "leaderboard_name"];
  return ["first_name", "last_name", "contact_email"];
}

export function captureForTemplate(template: ShowTemplateId): CaptureOffer {
  if (template === "promo_game") return "both";
  if (template === "onsite_hall") return "onsite";
  return "public";
}

export function applyShowTemplateLegal(
  setup: LegalSetup,
  activationId: string,
  template: ShowTemplateId,
): LegalSetup {
  const named: LegalSetup = { ...setup, showTemplate: template };
  if (template === "promo_game") {
    const withGame: LegalSetup = {
      ...named,
      packs: {
        UK: applyPreset(named.packs.UK, "UK", "game"),
        US: applyPreset(named.packs.US, "US", "game"),
      },
    };
    return withRideLegalDocuments(withGame, activationId);
  }
  return {
    ...named,
    packs: {
      UK: { ...applyPreset(named.packs.UK, "UK", "enquiry"), privacyMode: "notice_only" },
      US: { ...applyPreset(named.packs.US, "US", "enquiry"), privacyMode: "notice_only" },
    },
  };
}
